import type { ModuleManifest } from "./manifest-registry";
import type { SlotId } from "./slot-id.type";
import type { SlotPermission } from "./slot-permission.type";
import type { SlotDefinition } from "./slot-definition.type";
import type { SlotContributions } from "./slot-contributions.type";

/** One contribution as a host reads it: the contributing module's id and the contribution itself. */
export type SlotEntry<S extends SlotId> = S extends SlotId ? { moduleId: string; slot: S } & SlotContributions[S] : never;

/** What modules add to one slot, after the gate and in order. A contribution is kept only when its own module is really on, the slot's host module is on, and `allowed` says the viewer holds its permission. Lowest `order` first; ties keep the registry order. The host renders the entries; it never imports the contributing modules. */
export function readSlot<S extends SlotId>(manifests: readonly ModuleManifest[], slots: readonly SlotDefinition[], on: ReadonlySet<string>, allowed: (permission: SlotPermission) => boolean, slotId: S): SlotEntry<S>[] {
  const host = slots.find((s) => s.id === slotId)?.host;
  if (!host || !on.has(host)) return [];
  return manifests
    .filter((m) => on.has(m.id))
    .flatMap((m) => m.contributes.filter((c): c is Extract<typeof c, { slot: S }> => c.slot === slotId).map((c) => ({ ...c, moduleId: m.id }) as unknown as SlotEntry<S>))
    .filter((c) => !c.permission || allowed(c.permission))
    .sort((a, b) => a.order - b.order);
}
